import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Text,
} from "flicket-ui";
import { motion } from "framer-motion";
import { useContext, useMemo } from "react";
import { BaseContext, UserContext } from "~context";
import { useReservationStore } from "~features/seated-reservation/store";
import * as actions from "~features/seated-reservation/store/actions/index";
import {
  FeeType,
  Permission,
  ReferralCampaign,
  ReferralRewardType,
  TicketTypeKind,
} from "~graphql/sdk";
import { usePermissions, useReferral } from "~hooks";
import { OrganizationFeatures } from "~lib";
import Laybuy from "../common/common.Laybuy";
import { getTicketFeeProperties } from "../common/common.utils";
import { AddonListItem } from "./pricingItems/PricingListItemAddon";
import { ListItem } from "./pricingItems/PricingListItem";

export const Pricing = ({
  type,
  bookingFee,
  bookingFeeType,
  changingSeatsFee,
  addons,
  changeTickets,
  navigateCheckout,
  isNavigating,
  onBack,
  laybuyGateway,
  priceComp,
  allowPublicComp,
  activeReferralCampaign,
}: {
  activeReferralCampaign: ReferralCampaign;
} & Partial<React.ComponentProps<any>>) => {
  const noSeatsAvailable = useReservationStore((state) => state.selectFailed);
  const busyState = useReservationStore((state) => state.busyState);
  const seats = useReservationStore((state) => state.seats);
  const selectedAddons = useReservationStore((state) => state.addons);
  const ticketTypes = useReservationStore((state) => state.eventTicketTypes);
  const availableTicketTypes = useReservationStore(
    (state) => state.availableTicketTypes
  );
  const validSeatSelection = useReservationStore(
    (state) => state.validSeatSelection
  );

  const checkPermissions = usePermissions();
  const { calculateReferralDiscount } = useReferral();

  const { isPOS, isAdmin, user } = useContext(UserContext);
  const { organization } = useContext(BaseContext);

  const showHiddenFees = !!organization.features?.includes(
    OrganizationFeatures.ShowHiddenFees
  );

  const ticketingOptions = useMemo((): any[] => {
    // Filter entire list of ticket types by permissions
    let filteredTicketTypes =
      (isAdmin && checkPermissions(Permission.OrderComp)) || allowPublicComp
        ? ticketTypes
        : ticketTypes?.filter(({ kind }) => kind === TicketTypeKind.Standard);

    // Filter the above ticket types further based on
    // the current Zone or Section availability
    filteredTicketTypes = filteredTicketTypes?.filter(
      (ticketType) =>
        !!availableTicketTypes?.find(
          ({ id, ticketTypeId, isActive }) =>
            (ticketType?.id === id || ticketType?.id === ticketTypeId) &&
            (typeof isActive !== "undefined" ? isActive : true)
        )
    );

    return filteredTicketTypes?.length
      ? filteredTicketTypes.map(({ id, name, ...rest }) => ({
          label: name.replace(/ticket/i, ""),
          value: id,
          ...rest,
        }))
      : [];
  }, [ticketTypes, availableTicketTypes, isAdmin]);

  const availableAddons = useMemo(
    () => addons?.filter(({ isActive }) => isActive),
    [addons]
  );

  const total = (() => {
    let _total = 0;

    seats?.forEach((seat) => {
      let price = seat.price;

      const {
        ticketFee,
        ticketFeeType,
        stadiumLevyFee,
      } = getTicketFeeProperties(seat.ticketType);

      if (showHiddenFees && ticketFee > 0) {
        if (ticketFeeType === FeeType.Percentage) {
          price *= ticketFee / 100;
        } else {
          price += ticketFee;
        }
      }

      _total += price + stadiumLevyFee;
    });

    availableAddons?.forEach((addon) => {
      const quantity = selectedAddons?.[addon.id];

      if (quantity) {
        _total += addon.price * quantity;
      }
    });

    if (priceComp) {
      _total -= priceComp;

      if (changingSeatsFee) {
        _total += changingSeatsFee;
      }
    }

    if (showHiddenFees && bookingFee > 0) {
      if (bookingFeeType === FeeType.Percentage) {
        _total *= bookingFee / 100;
      } else {
        _total += bookingFee;
      }
    }

    return _total;
  })();

  const updateSeatType = (seatId, ticketType) => {
    actions.updateSeatType({ seatId, ticketType });
  };

  const updateAddon = (id, quantity) => {
    actions.updateAddon({ id, quantity });
  };

  let pricingOptions;

  if (busyState === "finding-best-seats") {
    return (
      <motion.div
        key="spinner"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Spinner size={48} color="P300" mb={2} />
      </motion.div>
    );
  } else if (seats?.length) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeatType}
        options={ticketingOptions}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else if (type === "membership") {
    pricingOptions = <Text mb={3}>Please select your seating</Text>;
  } else if (noSeatsAvailable) {
    pricingOptions = (
      <>
        <Text mb={3}>Not enough seats available in this zone</Text>
        <PrimaryButton
          onClick={onBack}
          boxShadow="button"
          mt={{ _: "6/4", md: 1 }}
          w="100%"
        >
          Clear selection
        </PrimaryButton>
      </>
    );
  }

  const totalPrice =
    total - calculateReferralDiscount(activeReferralCampaign, total);

  return (
    <motion.div
      key="prices"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box>
        <Flex justifyContent={"space-between"} alignItems="baseline" mb={4}>
          <Text
            fontWeight="heavy"
            fontSize={5}
            color="N800"
            lineHeight="normal"
          >
            Your selection
          </Text>
        </Flex>

        {!seats?.length && (
          <Box mb={2} mt={4}>
            <Text fontWeight="extraBold" fontSize={3} color="N700">
              No seats selected
            </Text>
          </Box>
        )}

        {pricingOptions}

        <Box>
          {!noSeatsAvailable && (seats?.length || type === "membership") && (
            <>
              {!!availableAddons?.length && (
                <Box mb={2} mt={4}>
                  <Text
                    fontWeight="heavy"
                    fontSize={6}
                    color="N800"
                    lineHeight="normal"
                  >
                    Available Addons
                  </Text>
                  <Divider my={2} />
                  {availableAddons.map(
                    ({
                      id,
                      name,
                      price,
                      description,
                      restrictions,
                      addonImage,
                    }) => (
                      <AddonListItem
                        description={description}
                        restrictions={restrictions}
                        isAdmin={isAdmin}
                        addonImage={addonImage}
                        key={id}
                        name={name}
                        price={price}
                        quantity={selectedAddons?.[id]}
                        onChange={(quanity) => {
                          updateAddon(id, quanity);
                        }}
                      />
                    )
                  )}
                </Box>
              )}

              {!!changeTickets?.length && (
                <Box mb={2}>
                  <Divider />
                  {!!changingSeatsFee && (
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="extraBold" fontSize={3} color="N700">
                        Reservation change fee
                      </Text>
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        <Price price={changingSeatsFee} />
                      </Text>
                    </Flex>
                  )}

                  {!!priceComp && (
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="extraBold" fontSize={3} color="N700">
                        Price difference compensation
                      </Text>
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        <Price price={-priceComp} />
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}

              <Divider />

              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Total tickets
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {seats?.length}
                </Text>
              </Flex>

              {activeReferralCampaign &&
                activeReferralCampaign.referralUserRewardType ===
                  ReferralRewardType.Discount && (
                  <>
                    <Divider />
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        Sub-total
                      </Text>
                      <Text
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      >
                        {seats ? <Price price={total} /> : "-"}
                      </Text>
                    </Flex>

                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        Referral discount applied
                      </Text>
                      <Text
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      >
                        - $
                        {calculateReferralDiscount(
                          activeReferralCampaign,
                          total
                        ).toFixed(2)}
                      </Text>
                    </Flex>

                    <Divider />
                  </>
                )}

              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Total {showHiddenFees ? "(including booking fees)" : ""}
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {seats ? <Price price={totalPrice} /> : "-"}
                </Text>
              </Flex>

              {user?.credits > 0 && seats && (
                <>
                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Your credits to be deducted
                    </Text>
                    <Text
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    >
                      {`- $${user.credits.toFixed(2)}`}
                    </Text>
                  </Flex>

                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Total left to pay after deducting credits
                    </Text>
                    <Text
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    >
                      {totalPrice - user.credits > 0 ? (
                        <Price price={totalPrice - user.credits} />
                      ) : (
                        "$0.00"
                      )}
                    </Text>
                  </Flex>
                </>
              )}

              {laybuyGateway && !isPOS && totalPrice - user?.credits > 0 && (
                <Box mt={2} w="100%">
                  <Laybuy
                    totalPrice={totalPrice - user?.credits}
                    hasTicketSelected={!!seats.length}
                    showHiddenFees={showHiddenFees}
                    isSmallDisplay={true}
                    laybuy={laybuyGateway}
                  />
                </Box>
              )}

              <PrimaryButton
                boxShadow="button"
                mt={{ _: "6/4", md: 4 }}
                onClick={navigateCheckout}
                isLoading={isNavigating}
                disabled={
                  !ticketingOptions?.length ||
                  !seats?.length ||
                  (validSeatSelection === false && !isAdmin && !isPOS)
                }
                w="100%"
                fontSize={4}
              >
                {validSeatSelection === true || isAdmin || isPOS
                  ? "Buy tickets"
                  : "Can not leave a single seat"}
              </PrimaryButton>
            </>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};
